import React from 'react';
import {
  Alert,
  Button,
  Card,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { QuestionCircleOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { worktimeLimitsAPI } from '../services/api';

const { Text, Paragraph } = Typography;

const AUTOMATION_TOOLTIP =
  'Лимиты обновляются дважды: воскресенье 18:01 МСК (первичный расчёт по часам недели до вс 18:00, с этого момента открыт заказ на понедельник) и повторно в понедельник 00:01 МСК (подтянуть запоздавшие или пересчитанные часы за ту же неделю). Черновики корзин, которые не влезают в новый лимит, аннулируются; уже подтверждённые заказы прошлых дней не блокируют заказы на другие дни. Пороги часов фиксированы; суммы в рублях настраиваются ниже. Если у сотрудника задан лимит вручную, автоматика его не меняет, пока вы не снимете ручной лимит (очистите поля и сохраните). При ошибке синхронизации действуют прежние лимиты — будет показано предупреждение.';

type TierRow = {
  tier: number;
  label: string;
  dailyLimit: number;
  monthlyLimit: number;
  thresholdsReadonly?: boolean;
};

type LinkEmployee = {
  id: number;
  fullName?: string | null;
  username: string;
  worktimeUserId: number | null;
  linkStatus: string;
  lastWeekMinutes: number | null;
  limitSource: string | null;
  appliedTier: number | null;
  dailyLimit: number | null;
  monthlyLimit: number | null;
};

type WorktimeEmployee = {
  worktime_user_id: number;
  login: string;
  full_name: string;
  hours: number;
};

export const WorktimeAutomationCard: React.FC<{ compact?: boolean; onSynced?: () => void }> = ({
  compact = false,
  onSynced,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [automationEnabled, setAutomationEnabled] = React.useState(false);
  const [syncStale, setSyncStale] = React.useState(false);
  const [lastSyncAt, setLastSyncAt] = React.useState<string | null>(null);
  const [lastSyncError, setLastSyncError] = React.useState<string | null>(null);
  const [tiers, setTiers] = React.useState<TierRow[]>([]);
  const [tierDrafts, setTierDrafts] = React.useState<Record<number, { dailyLimit: number; monthlyLimit: number }>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await worktimeLimitsAPI.getSettings();
      setAutomationEnabled(!!data.automationEnabled);
      setSyncStale(!!data.syncStale);
      setLastSyncAt(data.lastSyncAt || null);
      setLastSyncError(data.lastSyncError || null);
      setTiers(data.tiers || []);
      const drafts: Record<number, { dailyLimit: number; monthlyLimit: number }> = {};
      (data.tiers || []).forEach((t: TierRow) => {
        drafts[t.tier] = { dailyLimit: t.dailyLimit, monthlyLimit: t.monthlyLimit };
      });
      setTierDrafts(drafts);
    } catch (error: any) {
      message.error('Не удалось загрузить настройки: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const saveAutomation = async (enabled: boolean) => {
    setSaving(true);
    try {
      const data = await worktimeLimitsAPI.updateSettings({ automationEnabled: enabled });
      setAutomationEnabled(!!data.automationEnabled);
      setSyncStale(!!data.syncStale);
      message.success(enabled ? 'Автоматизация включена' : 'Автоматизация выключена');
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const saveTiers = async () => {
    setSaving(true);
    try {
      const payload = tiers
        .filter((t) => t.tier > 0)
        .map((t) => ({
          tier: t.tier,
          dailyLimit: tierDrafts[t.tier]?.dailyLimit ?? t.dailyLimit,
          monthlyLimit: tierDrafts[t.tier]?.monthlyLimit ?? t.monthlyLimit,
        }));
      await worktimeLimitsAPI.updateTiers(payload);
      message.success('Градации сохранены');
      await load();
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message);
    } finally {
      setSaving(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const result = await worktimeLimitsAPI.syncNow();
      if (result.status === 'failed') {
        message.error(result.error || 'Синхронизация не выполнена');
      } else {
        message.success(
          `Синхронизация завершена: обновлено лимитов ${result.updated}, пропущено (ручные) ${result.skippedManual}, без привязки/часов ${result.unlinked}`
        );
      }
      await load();
      onSynced?.();
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message);
    } finally {
      setSyncing(false);
    }
  };

  const borderStyle = syncStale && automationEnabled
    ? { border: '2px solid #ff4d4f', borderRadius: 8 }
    : undefined;

  return (
    <Card
      loading={loading}
      style={borderStyle}
      title={
        <Space>
          <Switch
            checked={automationEnabled}
            loading={saving}
            onChange={(checked) => saveAutomation(checked)}
          />
          <span>Автоматизировать лимиты</span>
          <Tooltip title={AUTOMATION_TOOLTIP}>
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      }
      extra={
        !compact ? (
          <Button icon={<SyncOutlined />} loading={syncing} onClick={runSync}>
            Синхронизировать сейчас
          </Button>
        ) : null
      }
    >
      {syncStale && automationEnabled && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Синхронизация с WorkTime не выполнена"
          description={
            lastSyncError ||
            (lastSyncAt
              ? `Последняя успешная попытка: ${new Date(lastSyncAt).toLocaleString('ru')}. Используются прежние лимиты.`
              : 'Используются прежние лимиты.')
          }
        />
      )}

      {!compact && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Суммы лимитов по градациям (пороги часов за неделю до вс 18:00 фиксированы). При &le; 30 ч — 0 ₽ / день и 0 ₽ / месяц.
          </Paragraph>
          <Table
            rowKey="tier"
            size="small"
            pagination={false}
            dataSource={tiers}
            columns={[
              {
                title: 'Градация',
                dataIndex: 'label',
                key: 'label',
                width: 200,
              },
              {
                title: 'Дневной лимит, ₽',
                key: 'dailyLimit',
                render: (_: unknown, row: TierRow) =>
                  row.tier === 0 ? (
                    <Text>0</Text>
                  ) : (
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      value={tierDrafts[row.tier]?.dailyLimit}
                      onChange={(v) =>
                        setTierDrafts((prev) => ({
                          ...prev,
                          [row.tier]: {
                            dailyLimit: v ?? 0,
                            monthlyLimit: prev[row.tier]?.monthlyLimit ?? row.monthlyLimit,
                          },
                        }))
                      }
                    />
                  ),
              },
              {
                title: 'Месячный лимит, ₽',
                key: 'monthlyLimit',
                render: (_: unknown, row: TierRow) =>
                  row.tier === 0 ? (
                    <Text>0</Text>
                  ) : (
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      value={tierDrafts[row.tier]?.monthlyLimit}
                      onChange={(v) =>
                        setTierDrafts((prev) => ({
                          ...prev,
                          [row.tier]: {
                            dailyLimit: prev[row.tier]?.dailyLimit ?? row.dailyLimit,
                            monthlyLimit: v ?? 0,
                          },
                        }))
                      }
                    />
                  ),
              },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <Button type="primary" loading={saving} onClick={saveTiers}>
              Сохранить градации
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

const Limits: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [employees, setEmployees] = React.useState<LinkEmployee[]>([]);
  const [worktimeEmployees, setWorktimeEmployees] = React.useState<WorktimeEmployee[]>([]);
  const [linkSaving, setLinkSaving] = React.useState<Record<number, boolean>>({});

  const loadLinks = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await worktimeLimitsAPI.getLinks();
      setEmployees(data.employees || []);
      setWorktimeEmployees(data.worktimeEmployees || []);
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const saveLink = async (employeeId: number, worktimeUserId: number | null) => {
    setLinkSaving((prev) => ({ ...prev, [employeeId]: true }));
    try {
      await worktimeLimitsAPI.updateLink(employeeId, worktimeUserId);
      message.success('Привязка сохранена');
      await loadLinks();
    } catch (error: any) {
      message.error(error.response?.data?.error || error.message);
    } finally {
      setLinkSaving((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  const worktimeOptions = worktimeEmployees.map((wt) => ({
    value: wt.worktime_user_id,
    label: `${wt.full_name} (${wt.login}) — ${wt.hours} ч`,
  }));

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <WorktimeAutomationCard onSynced={loadLinks} />
      <Card
        title="Привязка сотрудников к WorkTime"
        extra={
          <Button icon={<ReloadOutlined />} onClick={loadLinks}>
            Обновить
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Сопоставление Luemen и WorkTime"
          description="Если логины или ФИО отличаются, выберите сотрудника WorkTime вручную. Колонка «Часы» заполняется при синхронизации (или сразу после выбора, после обновления сервера). Источник «Вручную» значит: автолимит для этого сотрудника не меняется — очистите ручной лимит в карточке сотрудника, если нужна автоматика."
        />
        <Table
          rowKey="id"
          loading={loading}
          pagination={false}
          dataSource={employees}
          scroll={{ x: 900 }}
          columns={[
            {
              title: 'Сотрудник Luemen',
              key: 'name',
              render: (_: unknown, row: LinkEmployee) => (
                <div>
                  <Text strong>{row.fullName || row.username}</Text>
                  <div style={{ fontSize: 12, color: '#999' }}>{row.username}</div>
                </div>
              ),
            },
            {
              title: 'WorkTime',
              key: 'link',
              width: 360,
              render: (_: unknown, row: LinkEmployee) => (
                <Select
                  allowClear
                  showSearch
                  placeholder="Выберите сотрудника WorkTime"
                  style={{ width: '100%' }}
                  options={worktimeOptions}
                  value={row.worktimeUserId ?? undefined}
                  optionFilterProp="label"
                  loading={!!linkSaving[row.id]}
                  onChange={(value) => saveLink(row.id, value ?? null)}
                />
              ),
            },
            {
              title: 'Часы (прошлая нед.)',
              key: 'hours',
              width: 140,
              render: (_: unknown, row: LinkEmployee) =>
                row.lastWeekMinutes != null ? `${(row.lastWeekMinutes / 60).toFixed(2)} ч` : '—',
            },
            {
              title: 'Источник лимита',
              key: 'source',
              width: 120,
              render: (_: unknown, row: LinkEmployee) =>
                row.limitSource === 'manual' ? 'Вручную' : row.limitSource === 'auto' ? 'Авто' : '—',
            },
            {
              title: 'Градация',
              dataIndex: 'appliedTier',
              width: 90,
              render: (tier: number | null) => (tier != null ? tier : '—'),
            },
            {
              title: 'Лимит сейчас',
              key: 'limitsNow',
              width: 160,
              render: (_: unknown, row: LinkEmployee) => {
                if (row.dailyLimit == null && row.monthlyLimit == null) return '—';
                const d = row.dailyLimit != null ? `${row.dailyLimit} ₽/день` : 'день —';
                const m = row.monthlyLimit != null ? `${row.monthlyLimit} ₽/мес` : 'мес —';
                const zero = (row.dailyLimit ?? 0) <= 0 && (row.monthlyLimit ?? 0) <= 0;
                return (
                  <span style={{ color: zero ? '#cf1322' : undefined }}>
                    {d}
                    <br />
                    {m}
                    {zero ? (
                      <>
                        <br />
                        <Text type="danger" style={{ fontSize: 12 }}>
                          заказ недоступен
                        </Text>
                      </>
                    ) : null}
                  </span>
                );
              },
            },
          ]}
        />
      </Card>
    </Space>
  );
};

export default Limits;
